import React from 'react';

/**
 * Shared prev/next pagination for admin / advisor case lists.
 */
export default function CasesListPagination({
  page,
  totalPages,
  totalItems,
  pageSize = 20,
  onPageChange,
  disabled = false,
  loading = false,
  itemLabelSingular = 'case',
  itemLabelPlural = 'cases',
  loadingLabel = 'Loading cases…',
}) {
  const safeTotalPages = Math.max(0, Number(totalPages) || 0);
  const safePage = Math.min(Math.max(1, Number(page) || 1), Math.max(1, safeTotalPages));
  const total = Number(totalItems) || 0;
  const isBusy = Boolean(disabled || loading);

  if (safeTotalPages <= 1 && total <= pageSize) {
    return total > 0 ? (
      <p className="text-sm text-slate-600 tabular-nums py-2">
        Showing {total} {total === 1 ? itemLabelSingular : itemLabelPlural}
      </p>
    ) : null;
  }

  const start = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end = Math.min(safePage * pageSize, total);

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 py-3 border-t border-slate-100"
      aria-busy={loading}
    >
      <p className="text-sm text-slate-600 tabular-nums flex items-center gap-2 min-w-0">
        {loading ? (
          <>
            <i className="ri-loader-4-line animate-spin text-emerald-600 shrink-0" aria-hidden />
            <span>{loadingLabel}</span>
          </>
        ) : (
          <>
            Showing {start}–{end} of {total}
            {safeTotalPages > 1 ? ` · Page ${safePage} of ${safeTotalPages}` : ''}
          </>
        )}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={isBusy || safePage <= 1}
          onClick={() => onPageChange(safePage - 1)}
          className="inline-flex min-h-[40px] items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 disabled:opacity-40 hover:bg-slate-50"
        >
          {loading ? (
            <i className="ri-loader-4-line animate-spin" aria-hidden />
          ) : (
            <i className="ri-arrow-left-s-line" aria-hidden />
          )}
          Previous
        </button>
        <button
          type="button"
          disabled={isBusy || safePage >= safeTotalPages}
          onClick={() => onPageChange(safePage + 1)}
          className="inline-flex min-h-[40px] items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 disabled:opacity-40 hover:bg-slate-50"
        >
          Next
          {loading ? (
            <i className="ri-loader-4-line animate-spin" aria-hidden />
          ) : (
            <i className="ri-arrow-right-s-line" aria-hidden />
          )}
        </button>
      </div>
    </div>
  );
}
